import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { useProfile } from "../contexts/ProfileContext";
import { useAuth } from "../contexts/AuthContext";
import { User } from "lucide-react";
import { usePostHog } from "posthog-js/react";

const CompleteProfileBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const colors = useColors();
  const { user } = useAuth();
  const posthog = usePostHog();
  const { isProfileComplete, needsOnboarding, isLoading } = useProfile();

  // Hide on certain pages (browse flats, signup, profile edit, static pages)
  const exactMatchPaths = ["/"];
  const startsWithPaths = [
    "/signup",
    "/profile/edit",
    "/about",
    "/support",
    "/privacy",
    "/eula",
    "/impressum",
    "/delete-data",
    "/faq",
    "/app/flat-view",
  ];

  const isHiddenForPath =
    exactMatchPaths.includes(location.pathname) ||
    startsWithPaths.some((path) => location.pathname.startsWith(path));

  const shouldShowBanner = !!user && !isLoading && !isProfileComplete && !isHiddenForPath;

  React.useEffect(() => {
    if (!shouldShowBanner) {
      return;
    }
    posthog?.capture("complete_profile_banner_shown", {
      surface: "complete_profile_banner",
      path: location.pathname,
      is_authenticated: !!user,
      needs_onboarding: needsOnboarding,
    });
  }, [location.pathname, needsOnboarding, posthog, shouldShowBanner, user]);

  if (isHiddenForPath) {
    return null;
  }

  // Don't show if not logged in, still loading, or profile is complete
  if (!user || isLoading || isProfileComplete) {
    return null;
  }

  const handleClick = () => {
    posthog?.capture("complete_profile_banner_clicked", {
      surface: "complete_profile_banner",
      path: location.pathname,
      is_authenticated: !!user,
      needs_onboarding: needsOnboarding,
      destination: needsOnboarding ? "signup" : "profile_edit",
    });
    if (needsOnboarding) {
      navigate("/signup");
    } else {
      navigate("/profile/edit");
    }
  };

  return (
    <div
      className="w-full px-4 py-3 flex items-center justify-between gap-4 cursor-pointer hover:opacity-90 transition-opacity"
      style={{ backgroundColor: colors.accentColor }}
      onClick={handleClick}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
        >
          <User size={20} color={colors.textNeutralOnPrimary} />
        </div>
        <div>
          <p
            className="font-semibold text-sm"
            style={{ color: colors.textNeutralOnPrimary }}
          >
            {t("complete_your_profile") || "Complete your profile"}
          </p>
          <p
            className="text-xs opacity-90"
            style={{ color: colors.textNeutralOnPrimary }}
          >
            {t("complete_profile_description") ||
              "Add your photo and info to start applying"}
          </p>
        </div>
      </div>
      <button
        className="px-4 py-2 rounded-full text-sm font-semibold transition-colors hover:opacity-90 flex-shrink-0"
        style={{
          backgroundColor: colors.textNeutralOnPrimary,
          color: colors.accentColor,
        }}
      >
        {t("finish_setup") || "Finish Setup"}
      </button>
    </div>
  );
};

export default CompleteProfileBanner;
